import React from "react";
import { Button, Form, Tab, Tabs } from "react-bootstrap";
import { updateSettings } from "../../lib/api";
import ContentSettings from "./ContentSettings";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { alertActions } from "../../features/alert/alertSlice";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { ISiteSettingsForm } from "../../types/forms";

const PageContent = (): React.JSX.Element | undefined => {
    const dispatch = useAppDispatch();
    const settings = useAppSelector(state => state.global.settings);

    const settingsToShow = {
        homePage: [
            "wibb_home_page_red_box",
            "wibb_home_content_1",
            "wibb_home_page_competition_levels",
            "wibb_home_page_testimonials",
        ],
        registration: ["wibb_team_registration_content"],
        vetting: ["wibb_vetting"],
    };

    const methods = useForm<ISiteSettingsForm>({
        mode: "onChange",
        defaultValues: {
            content: Object.keys(settingsToShow).reduce(
                (acc, page) => {
                    const settingValues =
                        settingsToShow[page as keyof typeof settingsToShow];
                    return settingValues.reduce((subAcc, settingName) => {
                        const setting = settings.find(
                            s => s.settingName === settingName,
                        );
                        if (setting) {
                            subAcc[settingName] = setting.setting;
                        }
                        return subAcc;
                    }, acc);
                },
                {} as Record<string, any>,
            ),
        },
    });

    const onSubmit: SubmitHandler<Partial<ISiteSettingsForm>> = async data => {
        const dirtyFields = methods.formState.dirtyFields;

        const filteredData: Partial<ISiteSettingsForm> = {
            content: {},
        };

        (Object.keys(dirtyFields) as Array<keyof ISiteSettingsForm>).forEach(
            category => {
                const dirtySubfields = dirtyFields[category];
                Object.keys(dirtySubfields).forEach(subfield => {
                    if (data[category]?.hasOwnProperty(subfield)) {
                        filteredData[category][subfield] =
                            data[category][subfield];
                    }
                });
            },
        );

        (Object.keys(filteredData) as Array<keyof ISiteSettingsForm>).forEach(
            category => {
                if (Object.keys(filteredData[category]).length === 0) {
                    delete filteredData[category];
                }
            },
        );

        await updateSettings(filteredData)
            .then(() => {
                dispatch(
                    alertActions.success({
                        title: "Settings updated successfully",
                        dismissible: true,
                    }),
                );
            })
            .catch(e =>
                dispatch(
                    alertActions.error({
                        title: "The server encountered a problem updating the settings.",
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                ),
            );
    };

    return (
        <section className="siteSettingsContent">
            <FormProvider {...methods}>
                <Form onSubmit={methods.handleSubmit(onSubmit)}>
                    <Tabs
                        defaultActiveKey="homepage"
                        id="siteSettings"
                        data-testid="siteSettingsContent"
                    >
                        <Tab eventKey="homepage" title="Home Page">
                            <ContentSettings
                                settings={settings.filter(
                                    s => s.category === "content",
                                )}
                                category="content"
                                settingsToShow={settingsToShow.homePage}
                            />
                        </Tab>
                        <Tab eventKey="registration" title="Team Registration">
                            <ContentSettings
                                settings={settings.filter(
                                    s => s.category === "content",
                                )}
                                category="content"
                                settingsToShow={settingsToShow.registration}
                            />
                        </Tab>
                        <Tab eventKey="vetting" title="Vetting Process">
                            <ContentSettings
                                settings={settings.filter(
                                    s => s.category === "content",
                                )}
                                category="content"
                                settingsToShow={settingsToShow.vetting}
                            />
                        </Tab>
                    </Tabs>
                    <Button variant="primary" type="submit">
                        Update Settings
                    </Button>
                </Form>
            </FormProvider>
        </section>
    );
};

export default PageContent;
